import { CommonModule, DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  IonApp,
  IonButton,
  IonButtons,
  IonIcon,
  IonInput,
  IonRouterOutlet,
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';
import { BannerComponent } from './components/banner/banner.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { WarningModalComponent } from './components/shared/modal/warning-modal/warning-modal.component';
import { FilterByCodePipe } from './components/shared/pipes/filter-by-code.pipe';
import { SharedModule } from './components/shared/shared.module';
import { ShipperConfigModule } from './components/shipper-config/shipper-config.module';
import { DisplayOnlyIconComponent } from './components/side-nav/display-only-icon/display-only-icon.component';
import { NavIconComponent } from './components/side-nav/nav-icon/nav-icon.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { SublinkIconComponent } from './components/side-nav/sublink-icon/sublink-icon.component';
import { TopNavComponent } from './components/top-nav/top-nav.component';
import { getMsalConfig } from './core/auth/auth-config';
import { OutgoingInterceptor } from './core/auth/outgoing.interceptor';
import { SecurityResponseInterceptor } from './core/auth/security-response.interceptor';
import { Language } from './core/models/languages.model';
import { environment } from './environments/environment';
import { CvisSwapComponent } from './SCI/components/cvis-swap/cvis-swap.component';
import { EdiSwapComponent } from './SCI/components/edi-swap/edi-swap.component';

export var currentLocale = signal(Language.ENGLISH.toString().toLowerCase());

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(getMsalConfig());
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiUrl, environment.B2E_scope);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

@NgModule({
  declarations: [
    AppComponent,
    WarningModalComponent,
    TopNavComponent,
    SideNavComponent,
    DisplayOnlyIconComponent,
    NavIconComponent,
    SublinkIconComponent,
    BannerComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    HomeComponent,
    SharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    MatMenuModule,
    FormsModule,
    MatTreeModule,
    MatRippleModule,
    MatIconModule,
    MatSidenavModule,
    MatDialogModule,
    MatTabsModule,
    MatInputModule,
    IonApp,
    IonRouterOutlet,
    MsalModule,
    IonIcon,
    IonButton,
    IonButtons,
    IonInput,
    EdiSwapComponent,
    CvisSwapComponent,
    FilterByCodePipe,
    ShipperConfigModule,
    LoginComponent,
  ],
  providers: [
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    HttpClient,
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular(),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: HTTP_INTERCEPTORS, useClass: OutgoingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SecurityResponseInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: LOCALE_ID,
      useValue: currentLocale(),
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
  ],
})
export class AppModule {}
